import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import { SearchBar } from "../components/SearchBar";
import { Pagination } from "../components/Pagination";

const LIMIT = 20;

async function extractErrorMessage(response, fallback) {
  try {
    const text = await response.text();
    if (!text) return fallback;
    try {
      const parsed = JSON.parse(text);
      return parsed?.error || parsed?.message || fallback;
    } catch {
      if (text.startsWith("<!DOCTYPE")) return fallback;
      return text;
    }
  } catch {
    return fallback;
  }
}

function emptyForm() {
  return {
    location: "",
    scenarios: "",
    population: "",
    area: "",
  };
}

function prettyJson(value) {
  if (!value) return "";
  const raw = typeof value === "string" ? value : JSON.stringify(value);
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function validateJson(text) {
  const raw = String(text || "").trim();
  if (!raw) return { valid: true, empty: true };
  try {
    JSON.parse(raw);
    return { valid: true, empty: false };
  } catch (error) {
    return { valid: false, error: error?.message || "Invalid JSON" };
  }
}

function mapRowToForm(row) {
  return {
    location: row?.location || "",
    scenarios: prettyJson(row?.scenarios),
    population: row?.population ?? "",
    area: row?.area ?? "",
  };
}

function normalizePayload(values) {
  const location = String(values.location || "").trim();
  const scenariosRaw = String(values.scenarios || "").trim();
  const populationRaw = String(values.population ?? "").trim();
  const areaRaw = String(values.area ?? "").trim();

  const population = populationRaw ? Number(populationRaw) : null;
  const area = areaRaw ? Number(areaRaw) : null;

  return {
    location,
    scenarios: scenariosRaw || null,
    population,
    area,
  };
}

export default function DefinedScenariosAdmin() {
  const { authFetch } = useAuth();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  const [createForm, setCreateForm] = useState(emptyForm());
  const [creating, setCreating] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());
  const [saving, setSaving] = useState(null);

  const totalPages = Math.max(1, Math.ceil((Number(total) || 0) / LIMIT));
  const createJsonState = useMemo(() => validateJson(createForm.scenarios), [createForm.scenarios]);
  const editJsonState = useMemo(() => validateJson(editForm.scenarios), [editForm.scenarios]);

  const handleFormatEditJson = () => {
    if (!editJsonState.valid) return;
    try {
      setEditForm((prev) => ({
        ...prev,
        scenarios: JSON.stringify(JSON.parse(String(prev.scenarios || "")), null, 2),
      }));
    } catch {
      // no-op
    }
  };

  const handleMinifyEditJson = () => {
    if (!editJsonState.valid) return;
    try {
      setEditForm((prev) => ({
        ...prev,
        scenarios: JSON.stringify(JSON.parse(String(prev.scenarios || ""))),
      }));
    } catch {
      // no-op
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    let alive = true;

    async function loadRows() {
      try {
        setLoading(true);
        setError(null);

        const params = new URLSearchParams({
          page: String(page),
          limit: String(LIMIT),
        });
        if (search) params.set("search", search);

        const response = await authFetch(`/api/scenathon/admin/definedscenarios?${params.toString()}`);
        if (!response.ok) {
          const msg = await extractErrorMessage(response, "Error loading defined scenarios");
          throw new Error(msg);
        }

        const data = await response.json();
        if (!alive) return;

        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotal(Number(data?.total) || 0);
      } catch (err) {
        if (!alive) return;
        console.error("Load defined scenarios error:", err);
        setError(err?.message || "Error loading defined scenarios");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadRows();

    return () => {
      alive = false;
    };
  }, [authFetch, page, search]);

  const onCreate = async () => {
    const payload = normalizePayload(createForm);

    if (!payload.location) {
      await Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "location is required",
      });
      return;
    }

    if (!createJsonState.valid) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid JSON",
        text: createJsonState.error,
      });
      return;
    }

    if ((payload.population !== null && !Number.isFinite(payload.population)) || (payload.area !== null && !Number.isFinite(payload.area))) {
      await Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "population and area must be numeric when provided",
      });
      return;
    }

    try {
      setCreating(true);

      const response = await authFetch("/api/scenathon/admin/definedscenarios", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const msg = await extractErrorMessage(response, "Error creating defined scenario");
        throw new Error(msg);
      }

      const data = await response.json();
      setCreateForm(emptyForm());
      setRows((prev) => [data.row, ...prev].slice(0, LIMIT));
      setTotal((prev) => prev + 1);

      await Swal.fire({
        icon: "success",
        title: "Created",
        text: "Defined scenario created successfully",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Create defined scenario error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error creating defined scenario",
      });
    } finally {
      setCreating(false);
    }
  };

  const onStartEdit = (row) => {
    setEditingId(row.id);
    setEditForm(mapRowToForm(row));
  };

  const onSaveEdit = async () => {
    const payload = normalizePayload(editForm);

    if (!payload.location) {
      await Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "location is required",
      });
      return;
    }

    if (!editJsonState.valid) {
      await Swal.fire({
        icon: "warning",
        title: "Invalid JSON",
        text: editJsonState.error,
      });
      return;
    }

    if ((payload.population !== null && !Number.isFinite(payload.population)) || (payload.area !== null && !Number.isFinite(payload.area))) {
      await Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "population and area must be numeric when provided",
      });
      return;
    }

    try {
      setSaving(editingId);

      const response = await authFetch(`/api/scenathon/admin/definedscenarios/${editingId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const msg = await extractErrorMessage(response, "Error updating defined scenario");
        throw new Error(msg);
      }

      const data = await response.json();
      setRows((prev) => prev.map((row) => (row.id === editingId ? data.row : row)));
      setEditingId(null);

      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Defined scenario updated successfully",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Update defined scenario error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error updating defined scenario",
      });
    } finally {
      setSaving(null);
    }
  };

  const onDelete = async (row) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete row?",
      text: `Location: ${row.location || row.id}`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    try {
      setSaving(row.id);

      const response = await authFetch(`/api/scenathon/admin/definedscenarios/${row.id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        const msg = await extractErrorMessage(response, "Error deleting defined scenario");
        throw new Error(msg);
      }

      setRows((prev) => prev.filter((item) => item.id !== row.id));
      setTotal((prev) => Math.max(0, prev - 1));

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Defined scenario deleted",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Delete defined scenario error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error deleting defined scenario",
      });
    } finally {
      setSaving(null);
    }
  };

  return (
    <div className="w-full p-4 md:p-6 font-montserrat space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Defined Scenarios</h2>
        <p className="text-gray-600">Manage location-level records from the definedscenarios table.</p>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-slate-900">{total}</span> rows
            </p>
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search by location or scenarios content..."
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <h2 className="text-sm font-semibold text-slate-800 mb-3">Create new row</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">location</span>
              <input
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={createForm.location}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, location: e.target.value }))}
                placeholder="location"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">population</span>
              <input
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={createForm.population}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, population: e.target.value }))}
                placeholder="population"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">area</span>
              <input
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={createForm.area}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, area: e.target.value }))}
                placeholder="area"
              />
            </label>
            <div className="hidden md:block" />
            <label className="block md:col-span-2">
              <div className="mb-1 flex items-center justify-between gap-3">
                <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500">scenarios</span>
                {!createJsonState.valid && <span className="text-xs text-red-500">{createJsonState.error}</span>}
              </div>
              <textarea
                rows={10}
                className={`w-full rounded border px-3 py-2 font-mono text-xs ${createJsonState.valid ? "border-slate-300" : "border-red-400 bg-red-50"}`}
                value={createForm.scenarios}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, scenarios: e.target.value }))}
                placeholder='{"MEX": {}}'
              />
            </label>
          </div>
          <div className="mt-3 flex justify-end">
            <button
              onClick={onCreate}
              disabled={creating}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Create row"}
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0">
        <div className="bg-white rounded-lg shadow-sm h-full overflow-auto">
          {loading ? (
            <div className="w-full p-6 flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" />
                <p className="text-gray-600">Loading defined scenarios...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-red-800 font-semibold mb-2">Error</h3>
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">No defined scenarios found.</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">id</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">location</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">population</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">area</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">scenarios</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-700">actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50 transition">
                    <td className="px-4 py-3 text-sm text-gray-700">{row.id}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{row.location || "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.population ?? "-"}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{row.area ?? "-"}</td>
                    <td className="px-4 py-3 text-xs text-gray-700 max-w-[420px]">
                      <div className="truncate" title={row.scenarios || ""}>{row.scenarios || "-"}</div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex gap-2">
                        <button
                          onClick={() => onStartEdit(row)}
                          disabled={saving === row.id}
                          className="rounded bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDelete(row)}
                          disabled={saving === row.id}
                          className="rounded bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <Pagination page={page} totalPages={totalPages} setPage={setPage} />
      </div>

      {editingId !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="font-semibold text-slate-900">Edit definedscenarios #{editingId}</h3>
              <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-700 text-xl leading-none">×</button>
            </div>

            <div className="overflow-y-auto px-6 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">location</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.location} onChange={(e) => setEditForm((prev) => ({ ...prev, location: e.target.value }))} placeholder="location" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">population</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.population} onChange={(e) => setEditForm((prev) => ({ ...prev, population: e.target.value }))} placeholder="population" />
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">area</span>
                  <input className="w-full rounded border border-slate-300 px-3 py-2 text-sm" value={editForm.area} onChange={(e) => setEditForm((prev) => ({ ...prev, area: e.target.value }))} placeholder="area" />
                </label>
                <div className="hidden md:block" />
                <div className="md:col-span-2">
                  <Field label="scenarios">
                    <JsonEditor
                      value={editForm.scenarios}
                      onChange={(next) => setEditForm((prev) => ({ ...prev, scenarios: next }))}
                      isValid={editJsonState.valid}
                      error={editJsonState.error}
                      onFormat={handleFormatEditJson}
                      onMinify={handleMinifyEditJson}
                    />
                  </Field>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200">
              <button onClick={() => setEditingId(null)} className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                Cancel
              </button>
              <button
                onClick={onSaveEdit}
                disabled={saving === editingId || !editJsonState.valid}
                className="rounded-lg bg-[#03203d] px-4 py-2 text-sm font-medium text-white hover:bg-[#05407b] disabled:opacity-50"
              >
                {saving === editingId ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
      {children}
    </label>
  );
}

function JsonEditor({ value, onChange, isValid, error, onFormat, onMinify }) {
  const lineCount = Math.max(1, String(value || "").split("\n").length);
  const lineNumbers = Array.from({ length: lineCount }, (_, i) => i + 1).join("\n");

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onFormat}
          className="rounded bg-slate-800 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700"
        >
          Format JSON
        </button>
        <button
          type="button"
          onClick={onMinify}
          className="rounded bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-300"
        >
          Minify
        </button>
        <span className={`text-xs font-medium ${isValid ? "text-emerald-700" : "text-red-700"}`}>
          {isValid ? "Valid JSON" : error || "Invalid JSON"}
        </span>
      </div>

      <div className="grid grid-cols-[auto,1fr] overflow-hidden rounded border border-slate-300 bg-white">
        <pre className="m-0 select-none border-r border-slate-200 bg-slate-50 px-2 py-2 text-right text-xs text-slate-400 font-mono leading-5">
          {lineNumbers}
        </pre>
        <textarea
          className="w-full min-h-48 resize-y border-0 px-3 py-2 text-sm font-mono leading-5 outline-none"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          spellCheck={false}
          placeholder='{"MEX": {}}'
        />
      </div>
    </div>
  );
}
